import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { CoverImagesProjectStatus } from '@/utils/enums';
import { RootState } from '@/utils/store';

import Progress from './Progress';

const Box = styled(motion.div)`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow:
    0px 10px 30px rgba(34, 34, 34, 0.12),
    0px 20px 100px rgba(34, 34, 34, 0.06);
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 12px 24px;
  position: sticky;
  bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
`;

const SummaryBoxes = styled.div`
  display: flex;
`;

const SummaryBox = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding-right: 24px;
  display: flex;
  align-items: center;
  margin-left: 24px;
  &:last-of-type {
    border-right: 0;
  }
  &:first-of-type {
    margin-left: 0;
  }
  > svg {
    margin-right: 16px;
  }
`;

const ImagesCount = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  border-radius: 9px;
  height: 18px;
  display: flex;
  align-items: center;
  margin-left: 8px;
  padding: 0 8px;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const ActionsHolder = styled.div`
  display: flex;
  > a,
  button {
    border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
    &:last-child {
      border-right: 0;
    }
  }
`;

const DownloadAll = styled(Text)`
  padding-right: 12px;
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const UnselectButton = styled(Text)`
  margin-left: 12px;
  transition: all ease-out 0.2s;
  min-width: 63px;
  text-align: left;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const SelectButton = styled(UnselectButton)`
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const PostButton = styled(Button)`
  padding: 0 16px;
  height: 46px;
`;

const renderIcon = () => {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        width="64"
        height="64"
        rx="8"
        fill="url(#paint0_linear_3652_75757)"
      />
      <path
        d="M42.6083 22H21.394C20.8614 22 20.4297 22.4477 20.4297 23V41C20.4297 41.5523 20.8614 42 21.394 42H42.6083C43.1408 42 43.5725 41.5523 43.5725 41V23C43.5725 22.4477 43.1408 22 42.6083 22Z"
        fill="#EFF1F6"
        stroke="#D3D6DF"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.4978 30.707L20.4297 36.9998L21.5 37.0923V41H42.5V38.9072L43.5725 38.9999L38.4687 33.707C38.3791 33.6141 38.2728 33.5404 38.1558 33.4902C38.0388 33.4399 37.9134 33.4141 37.7868 33.4141C37.6602 33.4141 37.5348 33.4399 37.4178 33.4902C37.3008 33.5404 37.1945 33.6141 37.105 33.707L34.6115 36.2927C34.522 36.3856 34.4157 36.4593 34.2987 36.5095C34.1817 36.5598 34.0563 36.5856 33.9297 36.5856C33.803 36.5856 33.6776 36.5598 33.5607 36.5095C33.4437 36.4593 33.3374 36.3856 33.2478 36.2927L27.8615 30.707C27.772 30.6141 27.6657 30.5404 27.5487 30.4902C27.4317 30.4399 27.3063 30.4141 27.1797 30.4141C27.053 30.4141 26.9276 30.4399 26.8107 30.4902C26.6937 30.5404 26.5874 30.6141 26.4978 30.707Z"
        fill="#D3D6DF"
      />
      <path
        d="M35.75 30.5C36.9926 30.5 38 29.4926 38 28.25C38 27.0074 36.9926 26 35.75 26C34.5074 26 33.5 27.0074 33.5 28.25C33.5 29.4926 34.5074 30.5 35.75 30.5Z"
        fill="#D3D6DF"
      />
      <defs>
        <linearGradient
          id="paint0_linear_3652_75757"
          x1="32"
          y1="0"
          x2="32"
          y2="64"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F2F3F7" />
          <stop offset="1" stopColor="#EBEDF3" />
        </linearGradient>
      </defs>
    </svg>
  );
};

interface Props {
  selectedImages: number[];
  unselectAll: () => void;
  onDownload: (selected: boolean) => Promise<void>;
  selectAll: () => void;
  onDownloadPlainImages?: () => Promise<void>;
  images: string[];
}

const FloatingBox: FC<Props> = ({
  unselectAll,
  selectAll,
  onDownload,
  onDownloadPlainImages,
  images,
  selectedImages,
}) => {
  const [isPosting, setIsPosting] = useState(false);

  const [progressCounter, setProgressCounter] = useState(0);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { title, slug, id, date_created, editor, is_open_list } = useSelector(
    (rootState: RootState) => rootState.coverImages,
  );

  const onPost = async () => {
    setIsPosting(true);
    try {
      const { projectId } = await api.post<any, { projectId: number }>(
        '/v1/cover-images/cover-images-projects',
        {
          article_title: title,
          article_id: id,
          slug,
          is_open_list,
          created_in_bp_at: date_created,
          editor_email: editor,
        },
      );

      const filteredImages = images?.filter((_, index) =>
        selectedImages.includes(index),
      );

      for (let image of filteredImages) {
        setProgressCounter((prev) => prev + 1);

        await api.post(`/v1/cover-images/post-to-article/${projectId}`, {
          image: image,
        });
      }

      await api.post(`/v1/cover-images/send-slack-notif/${projectId}`, {
        is_firs_message: true,
        status: CoverImagesProjectStatus.PENDING,
      });

      await queryClient.invalidateQueries(['new_cover_images_projects'], {
        exact: false,
      });

      setIsPosting(false);

      navigate(`/general/cover-images/${projectId}`);
    } catch (_) {
      alerts.error();
    }
    setIsPosting(false);
  };

  return (
    <>
      <Box>
        <SummaryBoxes>
          <SummaryBox>
            {renderIcon()}
            <div>
              <Heading $type="button">
                Generated Covers
                <ImagesCount as="span" $type="extraSmall" $color="white">
                  {images.length}
                </ImagesCount>
              </Heading>
              <ActionsHolder>
                <DownloadAll
                  onClick={() => onDownload(false)}
                  $color="gray"
                  $type="smallHeadline"
                  as="button"
                >
                  Download all
                </DownloadAll>
              </ActionsHolder>
            </div>
          </SummaryBox>
          <SummaryBox>
            {renderIcon()}
            <div>
              <Heading $type="button">
                Selected Covers
                <ImagesCount as="span" $type="extraSmall" $color="white">
                  {selectedImages.length ?? 0}
                </ImagesCount>
              </Heading>
              <ActionsHolder>
                <DownloadAll
                  onClick={() => onDownload(true)}
                  $color="gray"
                  $type="smallHeadline"
                  as="button"
                  disabled={!selectedImages.length}
                >
                  Download selected
                </DownloadAll>
                {!!selectedImages.length ? (
                  <UnselectButton
                    onClick={unselectAll}
                    $color="gray"
                    $type="smallHeadline"
                    as="button"
                  >
                    Unselect all
                  </UnselectButton>
                ) : (
                  <SelectButton
                    onClick={selectAll}
                    $color="gray"
                    $type="smallHeadline"
                    as="button"
                  >
                    Select all
                  </SelectButton>
                )}
              </ActionsHolder>
            </div>
          </SummaryBox>
          {!!onDownloadPlainImages && (
            <SummaryBox>
              {renderIcon()}
              <div>
                <Heading $type="button">
                  Plain Images
                  <ImagesCount as="span" $type="extraSmall" $color="white">
                    {selectedImages.length ?? 0}
                  </ImagesCount>
                </Heading>
                <ActionsHolder>
                  <DownloadAll
                    onClick={() => onDownloadPlainImages()}
                    $color="gray"
                    $type="smallHeadline"
                    as="button"
                    disabled={!selectedImages.length}
                  >
                    Download
                  </DownloadAll>
                </ActionsHolder>
              </div>
            </SummaryBox>
          )}
        </SummaryBoxes>
        {title && id && (
          <PostButton
            onClick={onPost}
            color="colorized"
            disabled={isPosting}
            loading={isPosting}
          >
            Send to Editor
          </PostButton>
        )}
      </Box>
      <AnimatePresence>
        {!!isPosting && (
          <Progress
            text="Uploading images..."
            progressPercent={
              (progressCounter /
                images.filter((_, index) => selectedImages.includes(index))
                  .length) *
              100
            }
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingBox;
